// Oberflächen-Manifest der Stammdaten: Routen, Navigation (design-system §3.2 – Kunden stehen beim Verkauf,
// Lieferanten beim Einkauf, Standorte und Grunddaten in den Einstellungen), Suche, Scan-Ziel, Nachschlage-Quellen.
// Wird von scripts/gen-registry.ts in Node geladen: Seiten nur per lazy-Import, keine Bausteine, kein CSS.
import { defineWebModule } from '@web/module'
import { Database, Factory, FileUp, FolderTree, MapPin, Package, Users } from 'lucide-react'
import { barcodeLookupSource, itemLookupSource, partnerLookupSource } from './lookups'

const ITEM_READ = 'masterdata.item.read'
const CUSTOMER_READ = 'masterdata.customer.read'
const SUPPLIER_READ = 'masterdata.supplier.read'

export default defineWebModule({
  id: 'masterdata',
  routes: [
    { path: '/items', title: 'Artikel', permission: ITEM_READ, lazy: () => import('./pages/ItemListPage') },
    { path: '/items/groups', title: 'Warengruppen', lazy: () => import('./pages/ItemGroupsPage') },
    { path: '/items/scan/:code', title: 'Artikel suchen', permission: ITEM_READ, lazy: () => import('./pages/ScanPage') },
    { path: '/items/:id', title: 'Artikel', permission: ITEM_READ, lazy: () => import('./pages/ItemDetailPage') },
    { path: '/sales/customers', title: 'Kunden', permission: CUSTOMER_READ, lazy: () => import('./pages/CustomerListPage') },
    { path: '/sales/customers/:id', title: 'Kunde', permission: CUSTOMER_READ, lazy: () => import('./pages/CustomerDetailPage') },
    { path: '/purchasing/suppliers', title: 'Lieferanten', permission: SUPPLIER_READ, lazy: () => import('./pages/SupplierListPage') },
    { path: '/purchasing/suppliers/:id', title: 'Lieferant', permission: SUPPLIER_READ, lazy: () => import('./pages/SupplierDetailPage') },
    { path: '/partners/:id', title: 'Kunde oder Lieferant', lazy: () => import('./pages/PartnerRedirectPage') },
    { path: '/settings/masterdata', title: 'Grunddaten', lazy: () => import('./pages/SettingsPage') },
    { path: '/settings/masterdata/:section', title: 'Grunddaten', lazy: () => import('./pages/SettingsPage') },
    { path: '/settings/import', title: 'Daten importieren', lazy: () => import('./pages/ImportPage') },
  ],
  nav: [
    { section: 'items', label: 'Artikel', icon: Package, to: '/items', permission: ITEM_READ, order: 10, mobile: { slot: 'work', write: 'masterdata.item.write' } },
    { section: 'items', label: 'Warengruppen', icon: FolderTree, to: '/items/groups', permission: ITEM_READ, order: 20 },
    { section: 'purchasing', label: 'Lieferanten', icon: Factory, to: '/purchasing/suppliers', permission: SUPPLIER_READ, order: 40 },
    { section: 'sales', label: 'Kunden', icon: Users, to: '/sales/customers', permission: CUSTOMER_READ, order: 60 },
    { section: 'settings', label: 'Grunddaten', icon: Database, to: '/settings/masterdata', order: 20 },
    { section: 'settings', label: 'Import', icon: FileUp, to: '/settings/import', order: 70 },
  ],
  commands: [
    { id: 'masterdata.item.create', label: 'Artikel anlegen', keywords: ['neu', 'Sortiment', 'listen'], icon: Package, to: '/items?new=1', permission: 'masterdata.item.write' },
    { id: 'masterdata.customer.create', label: 'Kunden anlegen', keywords: ['neu', 'Debitor'], icon: Users, to: '/sales/customers?new=1', permission: 'masterdata.customer.write' },
    { id: 'masterdata.supplier.create', label: 'Lieferanten anlegen', keywords: ['neu', 'Kreditor'], icon: Factory, to: '/purchasing/suppliers?new=1', permission: 'masterdata.supplier.write' },
    { id: 'masterdata.groups', label: 'Warengruppen pflegen', keywords: ['Sortiment', 'Kategorie'], icon: FolderTree, to: '/items/groups', permission: ITEM_READ },
    { id: 'masterdata.locations', label: 'Standorte und Lagerplätze pflegen', keywords: ['Filiale', 'Lager', 'Regal'], icon: MapPin, to: '/settings/masterdata/locations', permission: 'masterdata.location.read' },
    { id: 'masterdata.import', label: 'Artikel, Kunden oder Lieferanten importieren', keywords: ['CSV', 'Excel', 'Datenübernahme', 'Migration'], icon: FileUp, to: '/settings/import' },
  ],
  searchResults: {
    item: { label: 'Artikel', icon: Package, to: (hit) => `/items/${hit.id}` },
    // Kunden und Lieferanten stehen in einer Gruppe; /partners/:id leitet auf die passende Seite
    partner: { label: 'Kunden & Lieferanten', icon: Users, to: (hit) => `/partners/${hit.id}` },
    location: { label: 'Standorte', icon: MapPin, to: (hit) => `/settings/masterdata/locations?open=${hit.id}` },
  },
  entityLinks: {
    item: (id) => `/items/${id}`,
    // Belege kennen nur die Partner-ID: /partners/:id schlägt nach, ob Kunde oder Lieferant, und leitet weiter
    partner: (id) => `/partners/${id}`,
    customer: (id) => `/sales/customers/${id}`,
    supplier: (id) => `/purchasing/suppliers/${id}`,
    location: (id) => `/settings/masterdata/locations?open=${id}`,
  },
  // ein gescannter Artikel-Barcode öffnet den Artikel (unbekannte Codes lassen sich dort zuordnen)
  // nur Strichcodes (GTIN-8 bis GTIN-14); Hausnummern wie „L-70004“ findet weiterhin die Suche
  scanHandlers: [
    {
      match: 'item-barcode',
      label: 'Artikel öffnen',
      to: (code) => `/items/scan/${encodeURIComponent(code)}`,
      permission: ITEM_READ,
      accepts: (code) => /^\d{8,14}$/.test(code),
    },
  ],
  lookups: { item: itemLookupSource, partner: partnerLookupSource, barcode: barcodeLookupSource },
})
